import type {ColumnFragment, ControlCardInstanceFullFragment, ValueFragment} from "app/gql/graphqlSchema";
import {assertNotNil, isEqual, isNotBlank} from "app/utils/stdlib";
import type {PrimaryRowValue} from "app/employee/controlCard/primaryKey/types";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import type {AsyncOrSync} from "ts-essentials";
import type {MaybeNil} from "app/utils/types";
import {IdComboBox} from "app/employee/controlCard/IdComboBox";
import {refMapping} from "app/employee/controlCard/refUtils";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardEmployeeRefColumn"};
  setValues: (value: ValueFragment[]) => AsyncOrSync<void>;
  values: PrimaryRowValue[];
}

type RefOption = ControlCardInstanceFullFragment["columnRefOptions"][number]["options"][number];

const keyExtractor = (option: RefOption) => option.ref.id;
const textExtractor = (option: RefOption) => option.employee.name;
const secondaryTextExtractor = (option: RefOption) => option.employee.position?.name ?? null;
const searchTextExtractor = (option: RefOption) =>
  [option.employee.name, option.employee.position?.name].filter(isNotBlank).join(" ");
const isRefOptionEqualToPrimaryRowValue = (option: RefOption, value: PrimaryRowValue) => {
  if (value.value.__typename === "ControlCardRefValue") {
    return option.ref.id === value.value.id;
  } else {
    return false;
  }
};

export const PrimaryRowEmployeeRefValue = ({column: primaryColumn, setValues, values}: Props) => {
  const {t} = useTranslation();
  const {instance, refresh} = useControlCardInstanceContext();

  const refOption = useMemo<RefOption[]>(() => {
    const refOption = instance.columnRefOptions.find((c) => isEqual(c.column.key, primaryColumn.key));
    assertNotNil(refOption);
    const {filter} = refMapping[refOption.__typename];

    return refOption.options.filter(filter);
  }, [instance.columnRefOptions, primaryColumn.key]);

  const handleSetValues = async (values: MaybeNil<RefOption[]>) => {
    await setValues(values?.map((v) => v.ref) ?? []);
  };

  return (
    <IdComboBox
      isEqual={isRefOptionEqualToPrimaryRowValue}
      keyExtractor={keyExtractor}
      multiselect
      onSelect={handleSetValues}
      options={refOption}
      refetch={refresh}
      searchPlaceholder={t("g.search-employee-name-or-position")}
      searchTextExtractor={searchTextExtractor}
      secondaryTextExtractor={secondaryTextExtractor}
      textExtractor={textExtractor}
      title={primaryColumn.title}
      value={values}
    />
  );
};
